/**
 * Jobs (admin) – job postings and applications
 */

import { apiClient } from '../client'
import type { PaginatedResponse } from './role.repository'

export interface Job {
  id: number
  title: string
  description: string | null
  status: string
  job_type: string | null
  salary_range: string | null
  category: string | null
  department?: string | null
  location: string | null
  open_positions: number | null
  requirements: string | null
  /** Candidate join date for this role (yyyy-MM-dd). */
  start_date: string | null
  workflow_status?: 'draft' | 'pending_approval' | 'approved' | 'rejected' | 'archived'
  is_published_mobile?: boolean
  approved_by?: number | null
  approved_at?: string | null
  rejection_reason?: string | null
  created_by?: number | null
  created_at: string
  updated_at: string
}

export interface JobApplication {
  id: number
  job_id: number
  user_id: number
  applied_at: string
  status: string
  created_at: string
  updated_at: string
  applicant_name?: string
  applicant_email?: string
  job_title?: string
}

interface JobsListResponse {
  data: Job[]
  pagination: { total: number; page: number; limit: number; pages: number }
}

interface JobApplicationsListResponse {
  data: JobApplication[]
  pagination: { total: number; page: number; limit: number; pages: number }
}

export const jobRepository = {
  async getAll(
    page: number = 1,
    limit: number = 10,
    filters?: { status?: string; job_type?: string; department?: string; workflow_status?: string }
  ): Promise<PaginatedResponse<Job>> {
    const params = new URLSearchParams({ page: String(page), limit: String(limit) })
    if (filters?.status) params.append('status', filters.status)
    if (filters?.job_type) params.append('job_type', filters.job_type)
    if (filters?.department) params.append('department', filters.department)
    if (filters?.workflow_status) params.append('workflow_status', filters.workflow_status)
    const res = await apiClient.get<JobsListResponse>(`/api/jobs?${params.toString()}`)
    return {
      data: res.data,
      total: res.pagination.total,
      page: res.pagination.page,
      limit: res.pagination.limit,
      totalPages: res.pagination.pages,
    }
  },

  async getById(id: number): Promise<Job> {
    const res = await apiClient.get<{ data: Job }>(`/api/jobs/${id}`)
    return res.data
  },

  /** Public (no auth): list jobs – for unauthenticated visitors */
  async getJobsPublic(
    page: number = 1,
    limit: number = 10,
    filters?: { status?: string; job_type?: string; department?: string; search?: string }
  ): Promise<PaginatedResponse<Job>> {
    const params = new URLSearchParams({ page: String(page), limit: String(limit) })
    if (filters?.status) params.append('status', filters.status)
    if (filters?.job_type) params.append('job_type', filters.job_type)
    if (filters?.department) params.append('department', filters.department)
    if (filters?.search?.trim()) params.append('search', filters.search.trim())
    const res = await apiClient.get<JobsListResponse>(`/api/jobs/public?${params.toString()}`)
    return {
      data: res.data,
      total: res.pagination.total,
      page: res.pagination.page,
      limit: res.pagination.limit,
      totalPages: res.pagination.pages,
    }
  },

  /** Public (no auth): get one job – for unauthenticated visitors */
  async getJobByIdPublic(id: number): Promise<Job> {
    const res = await apiClient.get<{ data: Job }>(`/api/jobs/public/${id}`)
    return res.data
  },

  async create(data: {
    title: string
    description?: string
    status?: string
    job_type?: string
    salary_range?: string
    category?: string
    department?: string
    location?: string
    open_positions?: number
    requirements?: string
    start_date?: string
  }): Promise<Job> {
    const res = await apiClient.post<{ data: Job }>('/api/jobs', data)
    return res.data
  },

  async update(
    id: number,
    data: {
      title?: string
      description?: string
      status?: string
      job_type?: string
      salary_range?: string
      category?: string
      department?: string
      location?: string
      open_positions?: number
      requirements?: string
      start_date?: string | null
    }
  ): Promise<Job> {
    const res = await apiClient.put<{ data: Job }>(`/api/jobs/${id}`, data)
    return res.data
  },

  async delete(id: number): Promise<void> {
    await apiClient.delete(`/api/jobs/${id}`)
  },

  async getApplicationsByJobId(
    jobId: number,
    opts?: { status?: string; search?: string }
  ): Promise<JobApplication[]> {
    const params = new URLSearchParams()
    if (opts?.status) params.set('status', opts.status)
    if (opts?.search?.trim()) params.set('search', opts.search.trim())
    const q = params.toString()
    const res = await apiClient.get<{ data: JobApplication[] }>(
      `/api/jobs/${jobId}/applications${q ? `?${q}` : ''}`
    )
    return res.data
  },

  async getApplicationsSummaryByJobId(jobId: number): Promise<Record<string, number>> {
    const res = await apiClient.get<{ data: Record<string, number> }>(
      `/api/jobs/${jobId}/applications/summary`
    )
    return res.data
  },

  async approve(id: number): Promise<Job> {
    const res = await apiClient.post<{ data: Job }>(`/api/jobs/${id}/approve`, {})
    return res.data
  },

  async reject(id: number, rejection_reason?: string): Promise<Job> {
    const res = await apiClient.post<{ data: Job }>(`/api/jobs/${id}/reject`, { rejection_reason })
    return res.data
  },

  async createBySupervisor(data: {
    title: string
    description?: string
    status?: string
    job_type?: string
    salary_range?: string
    category?: string
    department?: string
    location?: string
    open_positions?: number
    requirements?: string
    start_date?: string
  }): Promise<Job> {
    const res = await apiClient.post<{ data: Job }>('/api/user/jobs', data)
    return res.data
  },

  async submitForApproval(id: number): Promise<Job> {
    const res = await apiClient.post<{ data: Job }>(`/api/user/jobs/${id}/submit`, {})
    return res.data
  },
}

export const jobApplicationRepository = {
  async getAll(params: {
    page?: number
    limit?: number
    job_id?: number
    user_id?: number
    status?: string
    search?: string
  }): Promise<PaginatedResponse<JobApplication>> {
    const search = new URLSearchParams()
    if (params.page) search.set('page', String(params.page))
    if (params.limit) search.set('limit', String(params.limit))
    if (params.job_id) search.set('job_id', String(params.job_id))
    if (params.user_id) search.set('user_id', String(params.user_id))
    if (params.status) search.set('status', params.status)
    if (params.search?.trim()) search.set('search', params.search.trim())
    const res = await apiClient.get<JobApplicationsListResponse>(
      `/job-applications?${search.toString()}`
    )
    return {
      data: res.data,
      total: res.pagination.total,
      page: res.pagination.page,
      limit: res.pagination.limit,
      totalPages: res.pagination.pages,
    }
  },

  async getById(id: number): Promise<JobApplication> {
    const res = await apiClient.get<{ data: JobApplication }>(`/job-applications/${id}`)
    return res.data
  },

  async updateStatus(id: number, status: string): Promise<JobApplication> {
    const res = await apiClient.put<{ data: JobApplication }>(
      `/job-applications/${id}/status`,
      { status }
    )
    return res.data
  },
}
